/**
 * ApiGatewayYjsProvider - Custom Yjs sync provider for AWS API Gateway WebSocket
 *
 * Bridges the protocol gap between Yjs (binary sync) and our Lambda handlers
 * (JSON with base64). Provides the same interface as y-websocket's WebsocketProvider
 * so CardEditor.tsx can swap providers with minimal changes.
 *
 * Reference: .equilateral-standards/real-time-systems/websocket_postgres_pattern.md
 */

import * as Y from 'yjs';
import { Awareness, encodeAwarenessUpdate, applyAwarenessUpdate, removeAwarenessStates } from 'y-protocols/awareness';
import { Observable } from 'lib0/observable';

interface ProviderOptions {
  token: string;
  userId: string;
  userName: string;
  userColor?: string;
  maxReconnectAttempts?: number;
  pingIntervalMs?: number;
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

/**
 * Convert Uint8Array to base64 string
 */
function uint8ToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to Uint8Array
 */
function base64ToUint8(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export class ApiGatewayYjsProvider extends Observable<string> {
  doc: Y.Doc;
  awareness: Awareness;
  cardId: string;
  synced: boolean = false;

  private wsUrl: string;
  private token: string;
  private ws: WebSocket | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pingIntervalMs: number;
  private destroyed: boolean = false;
  private userId: string;
  private userName: string;
  private userColor: string;

  constructor(wsUrl: string, cardId: string, doc: Y.Doc, options: ProviderOptions) {
    super();
    this.doc = doc;
    this.cardId = cardId;
    this.wsUrl = wsUrl;
    this.token = options.token;
    this.userId = options.userId;
    this.userName = options.userName;
    this.userColor = options.userColor || '#4ECDC4';
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? 10;
    this.pingIntervalMs = options.pingIntervalMs ?? 270000; // 4.5 min (API GW timeout is 10 min)

    // Initialize Yjs Awareness for cursor presence
    this.awareness = new Awareness(doc);
    this.awareness.setLocalStateField('user', {
      id: this.userId,
      name: this.userName,
      color: this.userColor,
    });

    // Listen for local doc updates → send to server
    this.doc.on('update', this._handleDocUpdate);

    // Listen for awareness changes → broadcast to other clients
    this.awareness.on('update', this._handleAwarenessUpdate);

    // Connect
    this.connect();
  }

  /**
   * Establish WebSocket connection to API Gateway
   */
  connect(): void {
    if (this.destroyed) return;

    this.emit('status', [{ status: 'connecting' as ConnectionStatus }]);

    const url = `${this.wsUrl}?token=${encodeURIComponent(this.token)}&cardId=${encodeURIComponent(this.cardId)}`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error('[YjsProvider] WebSocket construction failed:', err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      console.log('[YjsProvider] Connected to', this.cardId);
      this.reconnectAttempts = 0;
      this.emit('status', [{ status: 'connected' as ConnectionStatus }]);

      // Request initial document sync
      this._send({
        action: 'yjs-sync-request',
        cardId: this.cardId,
      });

      // Start keepalive pings
      this._startPing();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data);
        this._handleMessage(msg);
      } catch (err) {
        console.error('[YjsProvider] Failed to parse message:', err);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      console.log('[YjsProvider] Disconnected:', event.code, event.reason);
      this._stopPing();
      this.emit('status', [{ status: 'disconnected' as ConnectionStatus }]);
      this.synced = false;

      if (!this.destroyed) {
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = (event: Event) => {
      console.error('[YjsProvider] WebSocket error:', event);
      // onclose will fire after onerror, triggering reconnect
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private _handleMessage(msg: {
    type: string;
    cardId?: string;
    update?: string;
    updates?: string[];
    awareness?: unknown;
    senderId?: string;
  }): void {
    switch (msg.type) {
      case 'yjs-sync-response':
        // Apply all stored updates (initial sync)
        if (msg.updates && Array.isArray(msg.updates)) {
          msg.updates.forEach((base64Update: string) => {
            try {
              const update = base64ToUint8(base64Update);
              Y.applyUpdate(this.doc, update, 'remote');
            } catch (err) {
              console.error('[YjsProvider] Failed to apply sync update:', err);
            }
          });
        }
        this.synced = true;
        this.emit('sync', [true]);
        console.log('[YjsProvider] Initial sync complete:', msg.updates?.length || 0, 'updates applied');
        break;

      case 'yjs-update':
        // Apply incremental update from another client
        if (msg.update) {
          try {
            const update = base64ToUint8(msg.update);
            Y.applyUpdate(this.doc, update, 'remote');
          } catch (err) {
            console.error('[YjsProvider] Failed to apply update:', err);
          }
        }
        break;

      case 'awareness-update':
        // Apply awareness state from another client
        if (msg.awareness && msg.senderId) {
          try {
            const update = base64ToUint8(msg.awareness as string);
            applyAwarenessUpdate(this.awareness, update, 'remote');
          } catch (err) {
            console.error('[YjsProvider] Failed to apply awareness:', err);
          }
        }
        break;

      default:
        console.log('[YjsProvider] Unknown message type:', msg.type);
    }
  }

  /**
   * Handle local document updates → send to server
   */
  private _handleDocUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === 'remote') return; // Don't echo remote updates back

    const base64Update = uint8ToBase64(update);
    this._send({
      action: 'yjs-update',
      cardId: this.cardId,
      update: base64Update,
      type: 'update',
    });
  };

  /**
   * Handle local awareness changes → broadcast to other clients
   */
  private _handleAwarenessUpdate = ({ added, updated, removed }: {
    added: number[];
    updated: number[];
    removed: number[];
  }, origin: unknown): void => {
    if (origin === 'remote') return;

    const changedClients = added.concat(updated).concat(removed);
    const awarenessUpdate = encodeAwarenessUpdate(this.awareness, changedClients);
    const base64Awareness = uint8ToBase64(awarenessUpdate);

    this._send({
      action: 'awareness-update',
      cardId: this.cardId,
      awareness: base64Awareness,
    });
  };

  /**
   * Send message via WebSocket
   */
  private _send(data: Record<string, unknown>): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  /**
   * Schedule reconnection with exponential backoff
   */
  private _scheduleReconnect(): void {
    if (this.destroyed || this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('[YjsProvider] Max reconnect attempts reached');
      }
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    console.log(`[YjsProvider] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Start keepalive ping to prevent API Gateway idle timeout (10 min)
   */
  private _startPing(): void {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      this._send({ action: 'ping', cardId: this.cardId });
    }, this.pingIntervalMs);
  }

  /**
   * Stop keepalive ping
   */
  private _stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    this.destroyed = true;
    this._stopPing();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Remove awareness for this client
    removeAwarenessStates(this.awareness, [this.doc.clientID], 'local');

    // Remove listeners
    this.doc.off('update', this._handleDocUpdate);
    this.awareness.off('update', this._handleAwarenessUpdate);

    // Close WebSocket
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    super.destroy();
  }
}
